import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ContainerIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import ContainerNav from './ContainerNav';

interface PackingList {
  import_id: number;
  filename: string;
  supplier_name: string;
  po_numbers: string[];
  uploaded_at: string;
  expected_delivery: string | null;
  stock_summary: StockSummary[];
  // Batch/merge fields
  effective_batch_id?: string;
  import_ids?: number[];
  filenames?: string[];
  batch_description?: string;
  is_merged?: boolean;
}

interface StockSummary {
  stock_code: string;
  po_number: string;
  total_pallets_reels: number;
  total_sheets?: number | null;
  total_metres?: number | null;
  total_weight_tonnes?: number | null;
  total_planned_quantity: number;
  planned_locations_count: number;
  preplans: Preplan[];
}

interface Preplan {
  location_name: string;
  planned_quantity: number;
  estimated_measurement_value?: number | null;
  estimated_measurement_unit?: string | null;
  notes?: string;
  created_by_staff_no?: string;
  created_at?: string;
}

const ContainerPlanning: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packingLists, setPackingLists] = useState<PackingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedPackingList, setExpandedPackingList] = useState<number | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [autoSaveTimers, setAutoSaveTimers] = useState<Record<string, NodeJS.Timeout>>({});

  // Filter state (matching Container Dashboard)
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  useEffect(() => {
    loadPackingLists();
  }, []);

  const loadPackingLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/containers-for-planning`);
      if (response.data.success) {
        setPackingLists(response.data.packing_lists || []);
      }
    } catch (err) {
      console.error('Error loading packing lists:', err);
      setError('Error loading packing lists for planning');
    } finally {
      setLoading(false);
    }
  };

  // Get unique suppliers for filter dropdown
  const uniqueSuppliers = Array.from(new Set(packingLists.map(pl => pl.supplier_name))).sort();

  // Filter packing lists
  const filteredPackingLists = packingLists.filter(pl => {
    // Search filter
    if (appliedSearchTerm) {
      const search = appliedSearchTerm.toLowerCase();
      const matchesFilename = pl.filename.toLowerCase().includes(search);
      const matchesPO = pl.po_numbers.some(po => po.toLowerCase().includes(search));
      const matchesStock = pl.stock_summary.some(s => s.stock_code.toLowerCase().includes(search));
      if (!matchesFilename && !matchesPO && !matchesStock) return false;
    }

    // Supplier filter
    if (supplierFilter && pl.supplier_name !== supplierFilter) return false;

    // Date filter
    if (dateFilter && pl.expected_delivery) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const eta = new Date(pl.expected_delivery);

      switch (dateFilter) {
        case 'today':
          if (eta.toDateString() !== today.toDateString()) return false;
          break;
        case 'next_week':
          const nextWeek = new Date(today);
          nextWeek.setDate(nextWeek.getDate() + 7);
          if (eta > nextWeek) return false;
          break;
        case 'next_two_weeks':
          const twoWeeks = new Date(today);
          twoWeeks.setDate(twoWeeks.getDate() + 14);
          if (eta > twoWeeks) return false;
          break;
        case 'next_month':
          const nextMonth = new Date(today);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          if (eta > nextMonth) return false;
          break;
      }
    }

    return true;
  });

  const handleAccordionChange = (import_id: number) => {
    setExpandedPackingList(expandedPackingList === import_id ? null : import_id);
  };

  // Auto-save function with debouncing
  const autoSavePreplan = useCallback(
    async (po_number: string, stock_code: string, location_name: string, preplan: Preplan, summary: StockSummary) => {
      if (!user?.staff_no) return;
      if (!location_name) return;

      const timerKey = `${po_number}-${stock_code}-${location_name}`;

      if (autoSaveTimers[timerKey]) {
        clearTimeout(autoSaveTimers[timerKey]);
      }

      const timer = setTimeout(async () => {
        try {
          setSaving(timerKey);

          const estimated_measurement_value = preplan.planned_quantity > 0 && summary.total_pallets_reels > 0
            ? ((summary.total_sheets ?? 0) > 0
                ? Math.round(((summary.total_sheets ?? 0) * preplan.planned_quantity) / summary.total_pallets_reels)
                : Math.round(((summary.total_metres ?? 0) * preplan.planned_quantity) / summary.total_pallets_reels))
            : null;
          const estimated_measurement_unit = (summary.total_sheets ?? 0) > 0 ? 'sheets' : 'metres';

          const response = await api.post(`/preplan/autosave`, {
            po_number,
            stock_code,
            location_name,
            planned_quantity: preplan.planned_quantity || 0,
            estimated_measurement_value,
            estimated_measurement_unit,
            notes: preplan.notes,
            created_by_staff_no: user.staff_no
          });

          if (response.data.success) {
            setPackingLists(prevLists => {
              const newLists = prevLists.map(pl => ({
                ...pl,
                stock_summary: pl.stock_summary.map(stock => {
                  if (stock.stock_code === stock_code && stock.po_number === po_number) {
                    return {
                      ...stock,
                      total_planned_quantity: response.data.new_total_planned || 0
                    };
                  }
                  return stock;
                })
              }));
              return newLists;
            });

            if (response.data.action === 'deleted') {
              setSuccess('Location removed');
            }
          }
        } catch (err: any) {
          console.error('Error auto-saving preplan:', err);
          setError(err.response?.data?.error || 'Error saving location');
        } finally {
          setSaving(null);
          setAutoSaveTimers(prev => {
            const newTimers = { ...prev };
            delete newTimers[timerKey];
            return newTimers;
          });
        }
      }, 500);

      setAutoSaveTimers(prev => ({ ...prev, [timerKey]: timer }));
    },
    [user, autoSaveTimers]
  );

  // Update local state only (called on every keystroke)
  const updatePreplanLocal = (
    importId: number,
    stock: StockSummary,
    preplanIndex: number,
    field: keyof Preplan,
    value: any
  ) => {
    setPackingLists(prevLists => {
      const newLists = prevLists.map(pl => {
        if (pl.import_id === importId) {
          return {
            ...pl,
            stock_summary: pl.stock_summary.map(s => {
              if (s.stock_code === stock.stock_code && s.po_number === stock.po_number) {
                const updatedPreplans = [...s.preplans];
                updatedPreplans[preplanIndex] = {
                  ...updatedPreplans[preplanIndex],
                  [field]: field === 'location_name' ? value.toUpperCase() : value
                };
                return {
                  ...s,
                  preplans: updatedPreplans
                };
              }
              return s;
            })
          };
        }
        return pl;
      });
      return newLists;
    });
  };

  // Save to server (called on blur/field exit)
  // Uses IDs to look up current state to avoid stale closure issues
  const savePreplanOnBlur = useCallback((
    importId: number,
    poNumber: string,
    stockCode: string,
    preplanIndex: number
  ) => {
    // Look up current state using the IDs
    setPackingLists(prevLists => {
      const packingList = prevLists.find(pl => pl.import_id === importId);
      if (!packingList) return prevLists;

      const stock = packingList.stock_summary.find(
        s => s.stock_code === stockCode && s.po_number === poNumber
      );
      if (!stock) return prevLists;

      const preplan = stock.preplans[preplanIndex];
      if (preplan && preplan.location_name) {
        // Call autoSavePreplan (this is a side effect inside setPackingLists,
        // but we're not modifying state here - just reading it)
        autoSavePreplan(
          stock.po_number,
          stock.stock_code,
          preplan.location_name,
          preplan,
          stock
        );
      }

      return prevLists; // Return unchanged state
    });
  }, [autoSavePreplan]);

  const addPreplanLocation = (importId: number, stock: StockSummary) => {
    setPackingLists(prevLists => {
      const newLists = prevLists.map(pl => {
        if (pl.import_id === importId) {
          return {
            ...pl,
            stock_summary: pl.stock_summary.map(s => {
              if (s.stock_code === stock.stock_code && s.po_number === stock.po_number) {
                return {
                  ...s,
                  preplans: [
                    ...s.preplans,
                    { location_name: '', planned_quantity: 0, notes: '' }
                  ]
                };
              }
              return s;
            })
          };
        }
        return pl;
      });
      return newLists;
    });
  };

  const removePreplan = async (
    importId: number,
    stock: StockSummary,
    preplanIndex: number
  ) => {
    const preplan = stock.preplans[preplanIndex];

    // If this preplan has a location, explicitly delete it from the database
    if (preplan.location_name && user?.staff_no) {
      try {
        setError(null);
        // Send explicit delete flag to backend (quantity=0 is now valid for "flexible" pre-plans)
        await api.post(`/preplan/autosave`, {
          po_number: stock.po_number,
          stock_code: stock.stock_code,
          location_name: preplan.location_name,
          planned_quantity: 0,
          delete: true,  // Explicit delete flag
          created_by_staff_no: user.staff_no
        });
      } catch (err: any) {
        console.error('Error deleting preplan:', err);
        setError('Error deleting location');
        return;
      }
    }

    setPackingLists(prevLists => {
      const newLists = prevLists.map(pl => {
        if (pl.import_id === importId) {
          return {
            ...pl,
            stock_summary: pl.stock_summary.map(s => {
              if (s.stock_code === stock.stock_code && s.po_number === stock.po_number) {
                const newPreplans = [...s.preplans];
                newPreplans.splice(preplanIndex, 1);
                return { ...s, preplans: newPreplans };
              }
              return s;
            })
          };
        }
        return pl;
      });
      return newLists;
    });
  };

  if (loading) {
    return (
      <Box p={3}>
        <ContainerNav />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <ContainerNav />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filter Section - matching Container Dashboard */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="PO Number, Container No, or Stock Code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={() => setAppliedSearchTerm(searchTerm)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedSearchTerm(searchTerm);
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Date Filter"
                >
                  <MenuItem value="">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="next_week">Next Week</MenuItem>
                  <MenuItem value="next_two_weeks">Next Two Weeks</MenuItem>
                  <MenuItem value="next_month">Next Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  label="Supplier"
                >
                  <MenuItem value="">All Suppliers</MenuItem>
                  {uniqueSuppliers.map((supplier) => (
                    <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Packing List Accordions */}
      {filteredPackingLists.length === 0 ? (
        <Box textAlign="center" py={8}>
          <ContainerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No packing lists found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload packing lists to begin pre-planning
          </Typography>
        </Box>
      ) : (
        filteredPackingLists
          .sort((a, b) => {
            // Sort by ETA ascending (earliest first), nulls last
            if (!a.expected_delivery && !b.expected_delivery) return 0;
            if (!a.expected_delivery) return 1;
            if (!b.expected_delivery) return -1;
            return new Date(a.expected_delivery).getTime() - new Date(b.expected_delivery).getTime();
          })
          .map((packingList) => {
            // Calculate totals for the packing list
            const totalPallets = packingList.stock_summary.reduce((sum, s) => sum + s.total_pallets_reels, 0);
            const totalPlanned = packingList.stock_summary.reduce((sum, s) => sum + (s.total_planned_quantity || 0), 0);
            const planningProgress = totalPallets > 0 ? Math.round((totalPlanned / totalPallets) * 100) : 0;

            return (
              <Accordion
                key={packingList.import_id}
                expanded={expandedPackingList === packingList.import_id}
                onChange={() => handleAccordionChange(packingList.import_id)}
                sx={{
                  mb: 1,
                  // Highlight merged batches with pink
                  ...(packingList.is_merged && {
                    borderLeft: '4px solid #e91e63',
                    '& .MuiAccordionSummary-root': {
                      backgroundColor: 'rgba(233, 30, 99, 0.04)'
                    }
                  })
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" width="100%" gap={2}>
                    {/* Merged badge */}
                    {packingList.is_merged && (
                      <Tooltip title={`Merged: ${packingList.filenames?.join(', ')}`}>
                        <Chip
                          icon={<FolderIcon />}
                          label={`${packingList.filenames?.length || 0} files`}
                          size="small"
                          sx={{
                            backgroundColor: '#e91e63',
                            color: 'white',
                            '& .MuiChip-icon': { color: 'white' }
                          }}
                        />
                      </Tooltip>
                    )}

                    <Chip
                      label={packingList.supplier_name}
                      size="small"
                      variant="outlined"
                    />

                    {packingList.po_numbers.map((po) => (
                      <Chip
                        key={po}
                        label={`PO ${po}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}

                    <Typography variant="h6" sx={{ minWidth: 200 }}>
                      {packingList.batch_description || packingList.filename}
                    </Typography>

                    {/* ETA */}
                    {packingList.expected_delivery && (
                      <Chip
                        label={`ETA: ${new Date(packingList.expected_delivery).toLocaleDateString('en-AU', {
                          weekday: 'short',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}`}
                        size="small"
                        sx={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          fontWeight: 500,
                          ml: 'auto'
                        }}
                      />
                    )}

                    {/* Planning Progress */}
                    <Chip
                      label={planningProgress === 100 ? 'Fully Planned' : `${planningProgress}% Planned`}
                      size="small"
                      color={planningProgress === 100 ? 'success' : planningProgress > 0 ? 'warning' : 'default'}
                      icon={planningProgress === 100 ? <CheckCircleIcon /> : undefined}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {/* Show nested file list for merged batches */}
                  {packingList.is_merged && packingList.filenames && packingList.filenames.length > 1 && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'rgba(233, 30, 99, 0.04)', borderColor: 'rgba(233, 30, 99, 0.3)' }}>
                      <Typography variant="subtitle2" sx={{ color: '#e91e63' }} gutterBottom>
                        Merged Files ({packingList.filenames.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {packingList.filenames.map((filename, idx) => (
                          <Chip
                            key={idx}
                            icon={<FileIcon />}
                            label={filename}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'rgba(233, 30, 99, 0.5)' }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  )}

                  {/* Stock Summary Table - sorted by quantity (largest first) */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Stock Code</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>PO</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Pallets/Reels</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty (sheets/kg)</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Planning Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', minWidth: 720 }}>Locations</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {packingList.stock_summary
                          .sort((a, b) => b.total_pallets_reels - a.total_pallets_reels)
                          .map((stock) => {
                            const isReel = !stock.stock_code.includes('X') && stock.stock_code.endsWith('F');
                            const packingListQty = isReel
                              ? `${((stock.total_weight_tonnes ?? 0) * 1000).toLocaleString()} kg`
                              : `${(stock.total_sheets ?? 0).toLocaleString()} sheets`;
                            const plannedQty = stock.total_planned_quantity || 0;
                            const isFullyPlanned = stock.total_pallets_reels > 0 && plannedQty >= stock.total_pallets_reels;
                            const currentUIPlanned = stock.preplans.reduce((sum, p) => sum + (p.planned_quantity || 0), 0);
                            const isOverPlanned = currentUIPlanned > stock.total_pallets_reels;

                            return (
                              <TableRow
                                key={`${stock.stock_code}-${stock.po_number}`}
                                sx={{
                                  backgroundColor: isOverPlanned ? '#ffebee' : isFullyPlanned ? '#e8f5e9' : 'inherit',
                                  '&:hover': { backgroundColor: isOverPlanned ? '#ffcdd2' : isFullyPlanned ? '#c8e6c9' : '#f5f5f5' }
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">
                                    {stock.stock_code}
                                  </Typography>
                                </TableCell>
                                <TableCell>{stock.po_number}</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold" color="primary">
                                    {stock.total_pallets_reels}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{packingListQty}</TableCell>
                                <TableCell align="center">
                                  {isFullyPlanned ? (
                                    <Chip label="Planned" color="success" size="small" icon={<CheckCircleIcon />} />
                                  ) : plannedQty > 0 ? (
                                    <Chip label={`${plannedQty}/${stock.total_pallets_reels}`} color="warning" size="small" />
                                  ) : (
                                    <Chip label="Not Planned" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {stock.preplans.length > 0 ? (
                                    <Box display="flex" flexDirection="column" gap={1}>
                                      {stock.preplans.map((preplan, idx) => (
                                        <Box key={idx} display="flex" alignItems="center" gap={1}>
                                          <TextField
                                            size="small"
                                            placeholder="Location"
                                            value={preplan.location_name}
                                            onChange={(e) => updatePreplanLocal(packingList.import_id, stock, idx, 'location_name', e.target.value)}
                                            onBlur={() => savePreplanOnBlur(packingList.import_id, stock.po_number, stock.stock_code, idx)}
                                            sx={{ width: 120 }}
                                          />
                                          <Tooltip title="Leave at 0 for flexible (operator decides quantity)">
                                            <TextField
                                              size="small"
                                              type="number"
                                              placeholder="Qty"
                                              value={preplan.planned_quantity === 0 ? '' : preplan.planned_quantity}
                                              onChange={(e) => updatePreplanLocal(packingList.import_id, stock, idx, 'planned_quantity', parseInt(e.target.value) || 0)}
                                              onBlur={() => savePreplanOnBlur(packingList.import_id, stock.po_number, stock.stock_code, idx)}
                                              inputProps={{ min: 0 }}
                                              sx={{ width: 100 }}
                                            />
                                          </Tooltip>
                                          {/* Show helper text for flexible pre-plans */}
                                          {preplan.planned_quantity === 0 && preplan.location_name && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', minWidth: 80 }}>
                                              Flexible
                                            </Typography>
                                          )}
                                          <TextField
                                            size="small"
                                            placeholder="Notes (Optional)"
                                            value={preplan.notes || ''}
                                            onChange={(e) => updatePreplanLocal(packingList.import_id, stock, idx, 'notes', e.target.value)}
                                            onBlur={() => savePreplanOnBlur(packingList.import_id, stock.po_number, stock.stock_code, idx)}
                                            sx={{ width: preplan.planned_quantity === 0 && preplan.location_name ? 370 : 450 }}
                                          />
                                          <IconButton size="small" onClick={() => removePreplan(packingList.import_id, stock, idx)} color="error">
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">No locations</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Add Location">
                                    <IconButton
                                      size="small"
                                      onClick={() => addPreplanLocation(packingList.import_id, stock)}
                                      color="primary"
                                    >
                                      <AddIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })
      )}
    </Box>
  );
};

export default ContainerPlanning;
